import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { getReadableOrderId } from 'pages/utils/HelperFun';

const API = 'https://backend.washta.com/api/residential/seller/orders';

const COMMISSION_PCT = 0.08;

const fmtAED = (value) => {
  const numeric = Number(value || 0);
  const decimals = !Number.isInteger(Math.round(numeric * 100) / 100);
  return `AED ${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(numeric)}`;
};

// Seller side of a package sale: package price − Washta commission (platform fee and Stripe are Washta's)
const packageMoney = (order) => {
  const gross = Number(order.pricePerMonth || 0);
  const commission = order.commissionAmount != null ? Number(order.commissionAmount) : gross * COMMISSION_PCT;
  return { gross, commission, payout: gross - commission }
};

const packageName = (order) => order.serviceNameSnapshot || `${order.washCount || 0} washes / month`;
const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);

// Groups orders by key and sums sales / money / washes, sorted by revenue
const groupBy = (orders, keyOf, labelOf) => {
  const map = new Map();
  orders.forEach((order) => {
    const key = keyOf(order);
    if (!map.has(key)) {
      map.set(key, { key, label: labelOf(order), sold: 0, active: 0, gross: 0, commission: 0, payout: 0, washesDone: 0, washesTotal: 0 });
    }
    const row = map.get(key);
    const money = packageMoney(order);
    row.sold += 1;
    if (order.status === 'active') row.active += 1;
    row.gross += money.gross;
    row.commission += money.commission;
    row.payout += money.payout;
    row.washesDone += Number(order.washesCompleted || 0);
    row.washesTotal += Number(order.washCount || 0);
  });
  return [...map.values()].sort((a, b) => b.gross - a.gross || b.sold - a.sold);
};

// Every wash delivered = package completed (the backend sets this too; covers orders saved before that)
const withCompletion = (order) =>
  order.status === 'active' && order.washCount > 0 && Number(order.washesCompleted || 0) >= order.washCount
    ? { ...order, status: 'completed' }
    : order;

// Loads every package order of the logged-in seller (filtered per period / location on the client)
export const useSellerPackageOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchOrders = async () => {
      try {
        const response = await axios.get(API, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        if (mounted) setOrders((response.data?.data || []).map(withCompletion));
      } catch (error) {
        console.error('Could not load seller package orders:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchOrders();
    return () => { mounted = false; };
  }, []);

  return { orders, loading };
};

// Full order (wash log, money) for the details popup
export const fetchPackageOrderDetails = async (order) => {
  try {
    const response = await axios.get(`${API}/${order._id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
    });
    return response.data?.data || order;
  } catch (error) {
    console.error('Could not load package order details:', error);
    return order;
  }
};

// Paid package orders in the dashboard's period ("today" / "week" / "month" / "all") and location ("shopId:locationId")
export const filterPackageOrders = (orders, period, locationKey) => orders.filter((order) => {
  if (order.billingStatus !== 'paid') return false;
  if (locationKey && locationKey !== 'all') {
    const [shopId, selectedLocationId] = locationKey.split(':');
    if (String(order.shopId) !== shopId || String(order.locationId) !== selectedLocationId) return false;
  }
  const date = new Date(order.paidAt || order.createdAt);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  if (period === 'today' && date.toDateString() !== now.toDateString()) return false;
  if (period === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    start.setHours(0, 0, 0, 0);
    if (date < start) return false;
  }
  if (period === 'month' && (date.getFullYear() !== now.getFullYear() || date.getMonth() !== now.getMonth())) return false;
  return true;
});

// Everything the dashboard needs about packages. Cancelled packages are not sales.
export const summarizePackages = (paidOrders) => {
  const sold = paidOrders.filter((order) => order.status !== 'cancelled');
  const totals = sold.reduce((t, order) => {
    const money = packageMoney(order);
    return {
      gross: t.gross + money.gross,
      commission: t.commission + money.commission,
      payout: t.payout + money.payout,
      washesDone: t.washesDone + Number(order.washesCompleted || 0),
      washesTotal: t.washesTotal + Number(order.washCount || 0),
    };
  }, { gross: 0, commission: 0, payout: 0, washesDone: 0, washesTotal: 0 });

  return {
    paidOrders,
    sold,
    soldCount: sold.length,
    activeCount: sold.filter((order) => order.status === 'active').length,
    finishedCount: sold.filter((order) => ['completed', 'expired'].includes(order.status)).length,
    cancelledCount: paidOrders.length - sold.length,
    ...totals,
    byPackage: groupBy(sold, (o) => packageName(o).toLowerCase(), packageName),
    byCarType: groupBy(sold, (o) => (o.carType || 'Unknown').toLowerCase(), (o) => o.carType || 'Unknown'),
    byLocation: groupBy(sold, (o) => `${o.shopId}:${o.locationId}`, (o) => o.locationDisplayName || 'Location'),
  };
};

export const EMPTY_PACKAGE_SUMMARY = summarizePackages([]);

export { fmtAED, packageMoney, packageName, pct };

export function PackageOrderDialog({ order, onClose }) {
  const money = order.money || {};
  const row = (label, value) => <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid #eef0f4', fontSize: 12 }}><span style={{ color: '#888' }}>{label}</span><strong style={{ textAlign: 'right' }}>{value || '—'}</strong></div>;
  const washLog = order.washLogEnriched || order.washLog || [];

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ style: { borderRadius: 18, background: '#fff', boxShadow: '0 24px 64px rgba(0,0,0,0.14)' } }}
    >
      <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #eef0f4', fontSize: 16, fontWeight: 700 }}>
        Package Order Details
        <IconButton onClick={onClose} size="small" aria-label="Close">✕</IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '14px 20px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <strong style={{ color: '#5144bd', fontFamily: 'monospace' }}>{order.orderId || getReadableOrderId(String(order._id), 'WS-PKG')}</strong>
          <span style={{ textTransform: 'capitalize', fontSize: 11, fontWeight: 700, color: '#5144bd', background: '#f0eeff', borderRadius: 20, padding: '4px 10px' }}>{order.status}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          <div>
            <h3 style={sectionTitle}>Package & customer</h3>
            {row('Customer', order.customerId?.fullName)}{row('Email', order.customerId?.email)}{row('Phone', order.customerId?.phone)}
            {row('Location', order.locationDisplayName)}{row('Package', order.serviceNameSnapshot || order.tierDisplayName || `${order.washCount || 0} washes / month`)}
            {row('Vehicle', [order.vehicleId?.vehicleManufacturer, order.vehicleId?.vehicleModel || order.vehicleId?.vehicleName, order.carType].filter(Boolean).join(' · '))}
            {row('Plate', order.vehicleId?.vehiclePlateNumber || order.plateNumber)}{row('Recurring days', order.recurringDaysLabel || (order.recurringDays || []).join(' · '))}
            {row('Period', order.startDate && order.endDate ? `${new Date(order.startDate).toLocaleDateString()} – ${new Date(order.endDate).toLocaleDateString()}` : null)}
            {row('Washes delivered', `${order.washesCompleted || 0} / ${order.washCount || 0}`)}
          </div>
          <div>
            <h3 style={sectionTitle}>Payment breakdown</h3>
            {row('Package price', `AED ${Number(money.packagePrice ?? order.pricePerMonth ?? 0).toFixed(2)}`)}
            {row('Platform fee', `AED ${Number(money.platformFee ?? order.platformFeeAmount ?? order.platformFee ?? 0).toFixed(2)}`)}
            {row('Customer charged', `AED ${Number(money.customerCharged ?? order.totalAmount ?? 0).toFixed(2)}`)}
            {row('Commission', `AED ${Number(money.commission ?? order.commissionAmount ?? 0).toFixed(2)}`)}
            {row('Stripe fee', `AED ${Number(money.stripeFee ?? 0).toFixed(2)}`)}{row('Washta net', `AED ${Number(money.washatNet ?? order.washatNet ?? 0).toFixed(2)}`)}
            {row('Provider payout', `AED ${Number(money.providerPayout ?? 0).toFixed(2)}`)}{row('Billing', order.billingStatus)}
            {row('Paid at', order.paidAt ? new Date(order.paidAt).toLocaleString() : null)}
          </div>
        </div>
        <h3 style={{ ...sectionTitle, marginTop: 20 }}>Wash delivery schedule</h3>
        {washLog.length ? washLog.map((wash, index) => <div key={wash._id || wash.washId || index} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 11 }}><span>{wash.scheduledDate ? new Date(wash.scheduledDate).toLocaleDateString() : '—'}</span><strong>{wash.status === 'due_today' ? 'Due today' : wash.status}</strong><span style={{ textAlign: 'right', color: '#888' }}>{wash.providerLabel || (wash.isCompensation ? 'Compensation wash' : '')}</span></div>) : <p style={{ fontSize: 11, color: '#888' }}>No wash schedule details available.</p>}
        {order.cancelReason && row('Cancellation reason', order.cancelReason)}
      </DialogContent>
    </Dialog>
  );
}

const sectionTitle = { fontSize: 12, color: '#999', textTransform: 'uppercase' };
